// addOverlays — installs every source + layer that the moving map renders.
//
// Extracted from MapView.tsx as HI-1 step 4 of the audit synthesis
// (2026-05-22). Before this extraction the function lived inline in
// MapView at 2,500 LOC, dwarfing every other concern in the file. The
// content is mechanical: ~30 GeoJSON sources, ~5 raster sources, and
// the layers that paint each one. No behavior change vs. the previous
// inline definition — same icons, same paint expressions, same layer
// order.
//
// Why a pure extraction now (instead of a registry-style spec rewrite):
//   - Lower risk: zero call-site or paint-property changes.
//   - Unblocks the registry-style migration: each chunk of this file
//     is now a 20-50 line block easy to cut into per-layer entries in
//     follow-up sessions.
//   - Drops MapView.tsx by ~50% (5,300 → ~2,800 LOC), making the
//     follow-up batches less merge-conflict-prone.
//
// Dependencies on MapView's module state: the function references
// `EMPTY_FC`, `applyFlightCatFilter`, `GFA_LAYER_IDS`, and
// `HAZARD_BY_LAYER` — all imported below. Everything else is module-
// scope from sibling map/ files.

import type { Map as MlMap } from 'maplibre-gl';
import { useUiStore } from '../stores/uiStore';
// DIPSITE_OWNERSHIP_COLOR_EXPR moved to src/map/layers/dipSites.ts (HI-1 batch 7 r4).
import { registerHatchPatterns } from './hatchPatterns';
// HI-1 batch 7 r2: ensurePirepIcons + PIREP_ICON_EXPRESSION moved to
// src/map/layers/pireps.ts along with the pireps layer family.
// HI-1 batch 6a: ensureWindBarbIcons + ensureSkyCoverIcons + their
// expression constants moved to src/map/layers/wxstations.ts along
// with the layers that use them. No longer imported here.
// HI-1 batch 5: ensureFixIcons and ensureUserWaypointIcons moved with
// their respective layer families to src/map/layers/.
// HI-1 batch 5 r2: ensureFuelIcons moved to src/map/layers/fuel.ts.
// HI-1 batch 5: ensureObstacleIcons and ensureRepeaterIcons moved
// with their layer families to src/map/layers/.
// HI-1 batch 7 round 5: buildGfaTimeFilter, GFA_LAYER_IDS, and
// HAZARD_BY_LAYER moved with the GFA layer family to src/map/layers/gfa.ts.
// HI-1 batch 8: ensureNavaidIcons + NAVAID_ICON_EXPRESSION moved to
// src/map/layers/waypoints.ts along with the waypoints layer family.
// HI-1 batch 8: EMPTY_FC + applyFlightCatFilter no longer used in this
// file — EMPTY_FC moved into each per-family module; applyFlightCatFilter
// moved to src/map/layers/wxstations.ts in batch 6a.
import {
  applyValidityWindowFilter,
} from './mapFilters';
// HI-1 batch 6a: wxstations layer family lives in its own module.
// Importing the module also registers its style-rebuild restore.
import { addWxStationsLayers } from './layers/wxstations';
// HI-1 batch 5: operational layers extracted into per-family modules.
// Each import also registers its layer family with the central registry.
// Round 1 (simple, single-source):
import { addObstaclesLayers } from './layers/obstacles';
import { addRepeatersLayers } from './layers/repeaters';
import { addWxcamsLayers } from './layers/wxcams';
import { addAirportDALayers } from './layers/airportDA';
import { addFixesLayers } from './layers/fixes';
import { addUserWaypointsLayers } from './layers/userWaypoints';
// Round 2 (larger operational set):
import { addTrackLayers } from './layers/track';
import { addTrafficLayers } from './layers/traffic';
import { addRangeRingsLayers } from './layers/rangeRings';
// IMPORT ORDER MATTERS: airports must be imported (and therefore mount-
// called) BEFORE fuel, because fuel binds layers to the airports source.
import { addAirportsLayers } from './layers/airports';
import { addHeliportsLayers } from './layers/heliports';
import { addFuelLayers } from './layers/fuel';
// HI-1 batch 6b: weather basemap raster families moved to per-file
// modules in src/map/layers/. Importing the module triggers its
// registerLayerFamily() side-effect; the addXxxLayers() call in this
// file performs the initial mount.
import { addAirmetLayers } from './layers/airmet';
import { addCwaLayers } from './layers/cwa';
import { addFiresLayers } from './layers/fires';
import { addNexradLayers } from './layers/nexrad';
import { addHrrrSmokeLayers } from './layers/hrrrSmoke';
import { addPirepsLayers } from './layers/pireps';
import { addSigmetLayers } from './layers/sigmet';
import { addSuaLayers } from './layers/sua';
import { addTfrLayers } from './layers/tfr';
import { addViirsLayers } from './layers/viirs';
import { addSfcAnalysisLayers } from './layers/sfcanalysis';
import { addSatGeoColorLayers } from './layers/satgeocolor';
import { addSatIrLayers } from './layers/satir';
import { addTerrainLayers } from './layers/terrain';
import { addClassBCDLayers } from './layers/classBCD';
import { addAirwaysLayers } from './layers/airways';
import { addDispatchZonesLayers } from './layers/dispatchZones';
import { addIhogRingsLayers } from './layers/ihogRings';
import { addDipSitesLayers } from './layers/dipSites';
import { addGfaLayers } from './layers/gfa';
// HI-1 batch 8 (final HI-1 batch): flight-plan families extracted into
// per-family modules under src/map/layers/.
import { addPlanLayers } from './layers/plan';
import { addPlanHoldingsLayers } from './layers/planHoldings';
import { addWaypointsLayers } from './layers/waypoints';

export function addOverlays(map: MlMap) {
  // HI-1 batch 7 round 1: fires moved to src/map/layers/fires.ts.
  addFiresLayers(map);

  // HI-1 batch 8: plan route line + waypoint pins moved to src/map/layers/plan.ts.
  addPlanLayers(map);

  // HI-1 batch 8: holding-pattern racetracks moved to src/map/layers/planHoldings.ts.
  addPlanHoldingsLayers(map);

  // Dispatch zones mount FIRST among the airspace/aviation layer
  // group so the lime-green boundary polygons sit underneath SUA,
  // Class B/C/D airspace, airways, TFRs, airports, and traffic.
  // Original ordering had dispatch mounting after airports; boss
  // flagged that the aviation symbology was being hidden behind the
  // dispatch fill. Moving dispatch up here keeps it as a background
  // overlay where it belongs.
  // HI-1 batch 7 round 3: dispatch-zones moved to src/map/layers/dispatchZones.ts.
  addDispatchZonesLayers(map);

  // HI-1 batch 7 round 1: sua moved to src/map/layers/sua.ts.
  addSuaLayers(map);

  // HI-1 batch 7 round 3: classBCD moved to src/map/layers/classBCD.ts.
  addClassBCDLayers(map);

  // HI-1 batch 7 round 3: airways moved to src/map/layers/airways.ts.
  addAirwaysLayers(map);

  // HI-1 batch 7 round 1: tfr moved to src/map/layers/tfr.ts.
  addTfrLayers(map);

  // Track log line (breadcrumb trail of recorded GPS fixes).
  // Bright neon green so it reads against sectional, satellite, and
  // topo basemaps alike — the original yellow vanished over wheat/dry
  // grass and competed with sectional VFR airspace tints.
  // HI-1 batch 5 round 2: track moved to src/map/layers/track.ts.
  addTrackLayers(map);

  // ADS-B traffic targets. Live aircraft positions from the internet
  // source (adsb.fi v3) by default; a future GDL90 receiver wave will
  // feed the SAME source without touching this layer. Targets carry a
  // `colorClass` property (above / near-above / coalt / near-below /
  // below / emergency) so the icon expression picks the right tinted
  // variant. `icon-rotate` reads true track straight off the feature.
  // HI-1 batch 5 round 2: traffic moved to src/map/layers/traffic.ts.
  addTrafficLayers(map);

  // Range rings around the own-ship. Three concentric geodesic circles
  // whose radii are recomputed (in MapView's GPS / zoom effects) to
  // match the current zoom — wide rings when zoomed out, tight rings
  // when zoomed in. Drawn as line geometries on a single source.
  // HI-1 batch 5 round 2: range-rings moved to src/map/layers/rangeRings.ts.
  addRangeRingsLayers(map);

  // Airports (bbox-filtered) — VFR-sectional-style symbology.
  // Icons (registered via ensureAirportIcons) keyed by AIRPORT_ICON_EXPRESSION:
  //   apt-towered  blue ring + crosshair (large / scheduled service)
  //   apt-svc      magenta ring + crosshair (medium GA)
  //   apt-basic    magenta open ring (small non-towered)
  //   apt-seaplane cyan ring + anchor (seaplane base)
  // HI-1 batch 5 round 2: airports moved to src/map/layers/airports.ts.
  addAirportsLayers(map);

  // Heliports — opt-in layer (default off) for HEMS / fire-aviation /
  // SAR pilots. NASR has 5,651 of them filtered off the airports layer
  // because they cluttered the en-route chart for fixed-wing pilots
  // (every hospital + police pad). Wiley Anderson feedback 2026-05-09.
  // minzoom 10 — a metro like LA has dozens of pads in the same square
  // mile, so the layer-level zoom gate is critical or the chart becomes
  // unreadable measles. Server-side bbox endpoint also gates at z>=10
  // so the wire payload stays bounded.
  // HI-1 batch 5 round 2: heliports moved to src/map/layers/heliports.ts.
  addHeliportsLayers(map);

  // FAA WeatherCams — opt-in. Single global GeoJSON source populated
  // once on first toggle; minzoom 8 keeps en-route view clean. Icon
  // is the camera silhouette registered in airportIcons.ts.
  // HI-1 batch 5: wxcams moved to src/map/layers/wxcams.ts.
  addWxcamsLayers(map);
  addAirportDALayers(map);

  // IFR named fixes (5-letter waypoints from NASR). Rendered as a
  // small filled BLUE TRIANGLE per FAA enroute chart convention.
  // Boss feedback 2026-05-10: previous purple dot didn't read as a
  // chart symbol and was easily mistaken for noise.
  // HI-1 batch 5: fixes moved to src/map/layers/fixes.ts.
  addFixesLayers(map);

  // User-defined custom waypoints (saved by the pilot via the long-press picker).
  // Rendered as ember-400 diamond markers (wpt-flag icon from userWaypointIcons.ts).
  // HI-1 batch 5: user-waypoints moved to src/map/layers/userWaypoints.ts.
  addUserWaypointsLayers(map);

  // HI-1 batch 8: waypoints (NASR navaids) moved to src/map/layers/waypoints.ts.
  addWaypointsLayers(map);

  // HI-1 batch 7 round 1: viirs moved to src/map/layers/viirs.ts.
  addViirsLayers(map);

  // HI-1 batch 7 round 2: airmet moved to src/map/layers/airmet.ts.
  addAirmetLayers(map);

  // HI-1 batch 7 round 2: sigmet moved to src/map/layers/sigmet.ts.
  addSigmetLayers(map);

  // HI-1 batch 7 round 5: GFA hazards (one source, 8 hazard families,
  // 19 sub-layers, time-scrubber filter seeding) moved to
  // src/map/layers/gfa.ts. addGfaLayers() seeds the initial slider
  // filter; the LayerFamilySpec.applyState re-applies visibility +
  // filter on every basemap rebuild.
  addGfaLayers(map);

  // Same first-apply for SIGMET (it was created above with no
  // validity-window filter) so the slider state seeded into the
  // store before mount is honored.
  {
    const initialOffsetH = useUiStore.getState().wxHazardOffsetH;
    applyValidityWindowFilter(map, ['sigmet-fill', 'sigmet-line'], initialOffsetH);
  }

  // HI-1 batch 7 round 2: cwa moved to src/map/layers/cwa.ts.
  addCwaLayers(map);
  // Apply the current time-scrubber filter to the freshly-added
  // CWA layers so the slider state from before mount is honored
  // (same pattern as SIGMET above).
  {
    const initialOffsetH = useUiStore.getState().wxHazardOffsetH;
    applyValidityWindowFilter(map, ['cwa-fill', 'cwa-line'], initialOffsetH);
  }

  // HI-1 batch 6a: wxstations cluster (flightcat / surfacewinds /
  // ceilvis / temperature / dewspread) moved to src/map/layers/wxstations.ts.
  addWxStationsLayers(map);

  // HI-1 batch 6b: weather basemap raster families moved to
  // src/map/layers/{nexrad,sfcanalysis,satgeocolor,satir,terrain}.ts.
  // Mount order preserved (nexrad → sfcanalysis → satgeocolor → satir
  // → terrain) so each module's `beforeId` candidate-list of
  // already-added raster layer ids resolves the same way it did
  // pre-extraction.
  addNexradLayers(map);

  // Phase 1: HRRR-Smoke surface PM2.5 concentration. Mounted after NEXRAD
  // so it sits at the same z-order as radar (weather raster stack).
  addHrrrSmokeLayers(map);
  addSfcAnalysisLayers(map);
  addSatGeoColorLayers(map);
  addSatIrLayers(map);
  addTerrainLayers(map);

  // HI-1 batch 5 round 2: fuel moved to src/map/layers/fuel.ts.
  addFuelLayers(map);

  // HI-1 batch 7 round 2: pireps moved to src/map/layers/pireps.ts.
  addPirepsLayers(map);

  // HI-1 batch 7 round 4: ihog-rings moved to src/map/layers/ihogRings.ts.
  addIhogRingsLayers(map);

  // HI-1 batch 7 round 4: dip-sites moved to src/map/layers/dipSites.ts.
  addDipSitesLayers(map);

  // ---- FAA Digital Obstacle File (tall obstacles) -------------------
  // Bbox-filtered point set from /api/obstacles. minzoom=8 keeps the
  // sectional view uncluttered when zoomed out for cross-country
  // planning; pilots flying low / scouting LZs will be zoomed in well
  // past 8 anyway. Labels (AGL ft) only at z>=10.
  // HI-1 batch 5: obstacles moved to src/map/layers/obstacles.ts.
  addObstaclesLayers(map);

  // ---- NIFC aviation-frequency repeater sites -----------------------
  // Static GeoJSON loaded from /data/nifc-repeaters.geojson, parsed
  // offline from the 2013 NIFC National Interagency Aviation Frequency
  // Guide. Each feature is a mountain-top repeater/receiver tied to a
  // wildland-fire dispatch center, with its primary channel (RX/TX/
  // tone) and the full list of channels available through that center.
  //
  // Visual: emerald antenna mast with radio waves — distinct from the
  // amber obstacle icons and the lime dispatch-zone polygons.
  // Two variants: real site (solid base) vs geocoded center pin
  // (dashed approximate ring around the base) picked by
  // REPEATER_ICON_EXPRESSION at draw time. minzoom=6 so the layer is
  // visible on a national overview; site-name labels at z>=9.
  // HI-1 batch 5: repeaters moved to src/map/layers/repeaters.ts.
  addRepeatersLayers(map);
}
